import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
} from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Eraser } from 'lucide-react';

import { ApiError } from '../api/client';
import {
  EnvironmentResetRun,
  EnvironmentResetSweep,
  getEnvironmentResetState,
  requestEnvironmentReset,
  sweepOrphanedWaveUsers,
} from '../api/environmentReset';
import { Fact, Mono, PanelShell } from '../components/detailPrimitives';
import { STATUS, STROKE, TEXT } from '../theme/tokens';

import EnvironmentEcho from '../components/EnvironmentEcho';
// FD-410 — the deployment's global settings page, with FD-ADR-039's environment
// reset as its first occupant. There was no global settings surface before this;
// only a per-group one.
//
// THE CONSOLE IS NOT THE ENFORCEMENT POINT. This page hides the reset entirely
// when `allowed` is false and keeps the button disabled until the typed name
// matches exactly — and the SERVER refuses independently on all three counts
// (403 ENVIRONMENT_RESET_DISABLED, 500 CONFIGURATION_ERROR, 400
// CONFIRMATION_MISMATCH). A console-only withhold is the FD-296 class of gate:
// one that only runs where someone happens to be standing, and therefore no gate
// at all against a script, a stale bundle, or a second client.
//
// DO NOT REMOVE THE SERVER-SIDE CHECKS on the grounds that this page already
// prevents the action. The UI preventing it is a convenience; the endpoint
// refusing it is the control.

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const body = error.body as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return error instanceof Error ? error.message : 'Unexpected error';
}

export default function SettingsPage() {
  const [confirmText, setConfirmText] = useState('');
  const [run, setRun] = useState<EnvironmentResetRun | null>(null);
  const [sweep, setSweep] = useState<EnvironmentResetSweep | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stateQuery = useQuery({
    queryKey: ['environment-reset-state'],
    queryFn: getEnvironmentResetState,
  });
  const state = stateQuery.data;

  // SETTLE-THEN-SCHEDULE, NEVER A FIXED INTERVAL. The FD-ADR-032 double-mint
  // lesson, arriving from the client: a `setInterval` that overlaps a slow call
  // issues a second one while the first is still in flight. There it minted a
  // second tenant-join credential; here it would only re-read, but the shape is
  // the one that produced that defect and the fix costs a ref and a timeout.
  const pollTimer = useRef<number | null>(null);
  const cancelled = useRef(false);

  const pollUntilDrained = useCallback(async () => {
    if (cancelled.current) return;
    try {
      const next = await getEnvironmentResetState();
      if (cancelled.current) return;
      stateQuery.refetch();
      if (next.migrationCount > 0) {
        // Only after the previous read has SETTLED is the next one scheduled.
        pollTimer.current = window.setTimeout(() => void pollUntilDrained(), 4000);
      }
    } catch {
      // A transient read failure stops the poll rather than spinning on it; the
      // operator can reload. Silence here is deliberate — a poll that keeps
      // retrying through an outage is how a page becomes a load generator.
    }
  }, [stateQuery]);

  useEffect(() => {
    cancelled.current = false;
    return () => {
      cancelled.current = true;
      if (pollTimer.current) window.clearTimeout(pollTimer.current);
    };
  }, []);

  if (stateQuery.isLoading) {
    return <Spinner label="Loading settings" />;
  }

  if (stateQuery.isError) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>{errorMessage(stateQuery.error)}</MessageBarBody>
      </MessageBar>
    );
  }

  const environmentName = state?.environmentName ?? '';
  // Exact match, not a normalised one. The server compares ordinally and
  // case-sensitively; a looser check here would enable a button the endpoint
  // then refuses, which reads as a broken page rather than as a guard.
  const confirmMatches = environmentName !== '' && confirmText.trim() === environmentName;

  async function onReset() {
    setError(null);
    setBusy(true);
    try {
      const result = await requestEnvironmentReset(confirmText.trim());
      setRun(result);
      setConfirmText('');
      void pollUntilDrained();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function onSweep() {
    if (!run) return;
    setError(null);
    setBusy(true);
    try {
      setSweep(await sweepOrphanedWaveUsers(environmentName, run.runId));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <div>
        <h1 className="font-display text-xl font-semibold" style={{ color: TEXT.primary }}>
          Settings
        </h1>
        <p className="mt-1 text-sm" style={{ color: TEXT.secondary }}>
          Deployment-wide configuration and maintenance.
        </p>
      </div>

      {/* THE SURFACE IS ABSENT — not disabled — when the capability is off. A
          greyed-out "reset everything" button on a production console is an
          invitation to find out why it is greyed out. */}
      {!state?.allowed ? (
        <PanelShell title="Environment reset">
          <p className="text-sm" style={{ color: TEXT.secondary }}>
            Not available on this deployment. Environment reset is a non-production
            capability and is disabled here.
          </p>
        </PanelShell>
      ) : (
        <PanelShell title="Environment reset">
          <MessageBar intent="warning" icon={<AlertTriangle size={16} strokeWidth={1.5} />}>
            <MessageBarBody>
              <MessageBarTitle>This is not reversible, and it reaches outside this deployment.</MessageBarTitle>
              <ul className="mt-1 list-disc pl-5 text-[13px]">
                {(state.blastRadius ?? []).map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </MessageBarBody>
          </MessageBar>

          <div className="grid grid-cols-2 gap-x-8 gap-y-3">
            <Fact label="Environment">
              <Mono>{environmentName || '—'}</Mono>
            </Fact>
            <Fact label="Migrations to remove">
              <Mono>{state.migrationCount}</Mono>
            </Fact>
          </div>

          {(state.retained ?? []).length > 0 && (
            <p className="text-[13px]" style={{ color: TEXT.secondary }}>
              Kept: {(state.retained ?? []).join('; ')}.
            </p>
          )}

          <div style={{ borderTop: `1px solid ${STROKE.subtle}`, paddingTop: '12px' }}>
            {/* FD-422 — the echo beside the typed confirmation, and this is the
                one surface where the two are a genuine cross-check rather than a
                repetition: the name typed below comes from the SERVER
                (`state.environmentName`), the name shown here comes from
                `config.json`. If those disagree the drift banner is already up,
                and an operator about to empty a deployment can see both. */}
            <div className="mb-3">
              <EnvironmentEcho label="This resets" />
            </div>
            <Field
              label={`Type ${environmentName || 'the environment name'} to confirm`}
              hint="The server checks this too — the button is a convenience, not the control."
            >
              <Input
                value={confirmText}
                onChange={(_, data) => setConfirmText(data.value)}
                placeholder={environmentName}
                disabled={busy}
              />
            </Field>
            <div className="mt-3 flex justify-end">
              <Button
                appearance="primary"
                icon={<Eraser size={16} strokeWidth={1.5} />}
                disabled={!confirmMatches || busy}
                onClick={() => void onReset()}
              >
                Reset this environment
              </Button>
            </div>
          </div>
        </PanelShell>
      )}

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {run && (
        <PanelShell title="Reset run">
          <div className="grid grid-cols-2 gap-x-8 gap-y-3">
            <Fact label="Run id">
              <Mono>{run.runId}</Mono>
            </Fact>
            <Fact label="Queued">
              <Mono>
                {run.migrationsQueued} of {run.migrationsFound}
              </Mono>
            </Fact>
            <Fact label="Remaining">
              <Mono>{state?.migrationCount ?? '—'}</Mono>
            </Fact>
            <Fact label="Requested by">{run.requestedBy}</Fact>
          </div>

          {run.enqueueFailures.length > 0 && (
            <MessageBar intent="warning">
              <MessageBarBody>
                {run.enqueueFailures.length} migration(s) could not be queued:{' '}
                {run.enqueueFailures.join(', ')}
              </MessageBarBody>
            </MessageBar>
          )}

          <p className="text-[13px]" style={{ color: TEXT.secondary }}>
            Run the orphan sweep once the migrations have drained. Before then every
            wave still looks live and the sweep would delete nothing.
          </p>
          <div className="flex justify-end">
            <Button disabled={busy} onClick={() => void onSweep()}>
              Sweep orphaned wave users
            </Button>
          </div>
        </PanelShell>
      )}

      {sweep && (
        <PanelShell title="Orphan sweep">
          <div className="grid grid-cols-4 gap-x-6 gap-y-3">
            <Fact label="Tenants swept">
              <Mono>{sweep.tenantsSwept}</Mono>
            </Fact>
            <Fact label="Deleted">
              <Mono>{sweep.deleted}</Mono>
            </Fact>
            <Fact label="Left alone">
              <Mono>{sweep.leftAlone}</Mono>
            </Fact>
            <Fact label="Failed">
              <Mono>{sweep.failed}</Mono>
            </Fact>
          </div>
          {/* Per user, never a bare count — a count cannot be acted on. */}
          <ul className="flex flex-col gap-1 text-[13px]">
            {sweep.results.map((r, i) => (
              <li key={`${r.tenantId}-${r.displayName ?? i}`} className="flex gap-2">
                <span
                  style={{
                    color: r.action === 'deleted' ? STATUS.success : r.action === 'failed' ? STATUS.error : STATUS.neutral,
                  }}
                >
                  {r.action}
                </span>
                <span style={{ color: TEXT.primary }}>{r.displayName ?? r.tenantId}</span>
                <span style={{ color: TEXT.secondary }}>— {r.reason}</span>
              </li>
            ))}
          </ul>
        </PanelShell>
      )}
    </div>
  );
}
